import { RefObject, useEffect, useLayoutEffect, useState } from 'react';
import { LETTER_HEIGHT_PX, LETTER_WIDTH_PX } from './page';

/** At this width and wider the editor and preview sit side by side. */
export const SPLIT_MIN_WIDTH = 1024;
export const SPLIT_MEDIA = `(min-width: ${SPLIT_MIN_WIDTH}px)`;
export const STACKED_MEDIA = `(max-width: ${SPLIT_MIN_WIDTH - 1}px)`;

const MIN_SCALE = 0.08;
const MAX_SCALE = 0.5;

export interface PreviewScaleInput {
  /** Width available to the preview, in CSS px. */
  width: number;
  /** Side-by-side layout: the preview must also fit the visible height. */
  split: boolean;
  /** Height of the visible area the preview has to fit in (split layout only). */
  viewportHeight: number;
  /** Vertical space taken by the preview header and padding. */
  reserved: number;
}

/** Scale that fits the 8.5x11in page into the space available. */
export const computePreviewScale = ({ width, split, viewportHeight, reserved }: PreviewScaleInput): number => {
  if (!width || width <= 0) return 0.25;
  let scale = width / LETTER_WIDTH_PX;
  if (split && viewportHeight > 0) {
    scale = Math.min(scale, (viewportHeight - reserved) / LETTER_HEIGHT_PX);
  }
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
};

export const useMediaQuery = (query: string, fallback = true): boolean => {
  const get = () => {
    try {
      return typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : fallback;
    } catch {
      return fallback;
    }
  };
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);
  return matches;
};

/** Live content width of an element. */
export const useElementWidth = (ref: RefObject<HTMLElement>): number => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

export const useWindowHeight = (): number => {
  const [height, setHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const update = () => setHeight(window.innerHeight);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return height;
};
